import { ILogger } from "@tdp/common/telemetry";
import { IPackageEditorReader } from "../abstractions/IPackageEditorReader";
import { createCommands } from "@tdp/common/commandHandler";
import { packageEditorSlice } from "./slice";
import { ApiFetchStatus } from "@tdp/common/constants";
import { AppPackageEditorStatus } from "../abstractions/EPackageEditor";
import { IAppPackageEditorFile } from "../abstractions/IPackageEditor";
import { notificationSlice, NotificationType } from "@tdp/common/notification";

export interface IPackageEditorCommandHandler {
  logger: ILogger;
  packageEditor: IPackageEditorReader;
}

export const createPackageEditorCommandHandler = ({ logger, packageEditor }: IPackageEditorCommandHandler) =>
  createCommands(
    {
      name: "packageEditorCommands",
      handlers: {
        // Inital App ID set
        async *setAppId(appId: string) {
          yield packageEditorSlice.actions.setAppId(appId);
        },
        // Reset State
        async *reset() {
          yield packageEditorSlice.actions.reset();
        },
        // Discard local files
        async *discardLocalFiles() {
          yield packageEditorSlice.actions.discardLocalFiles();
        },
        // Fetch App Package
        async *getAppPackage(appId: string) {
          const response = await packageEditor.getAppPackage(appId);
          if (response.type === ApiFetchStatus.SUCCESS && response.response) {
            yield packageEditorSlice.actions.setInitialPackageFiles(response.response);
          }
        },
        // Handle Status Change
        async *appPackageEditorStatusChange(s: AppPackageEditorStatus) {
          yield packageEditorSlice.actions.appPackageEditorStatusChange(s);
        },
        // Update a file
        async *appPackageEditorUpdateLocalFile(file: IAppPackageEditorFile) {
          yield packageEditorSlice.actions.appPackageEditorUpdateLocalFile(file);
        },
        // Remove file by id
        async *appPackageEditorRemoveLocalFileById(id: string) {
          yield packageEditorSlice.actions.appPackageEditorRemoveLocalFileById(id);
        },
        // Add a new file
        async *appPackageEditorAddLocalFile(file: IAppPackageEditorFile) {
          yield packageEditorSlice.actions.appPackageEditorAddLocalFile(file);
        },
        // Update using import
        async *updateUsingImport({ file, onCallback, onFail }: { file: File, onCallback: () => void, onFail: () => void }) {
          const response = await packageEditor.importApp(file);
          if (response.response) {
            yield notificationSlice.actions.showNotification({
              message: `App package updated successfully`,
              show: true,
              autoDismiss: true,
              type: NotificationType.Success,
            });
            onCallback();
          } else {
            yield notificationSlice.actions.showNotification({
              message: `Failed to update app package`,
              show: true,
              autoDismiss: true,
              type: NotificationType.Error,
            });
            onFail();
          }
        },
        // fetch schema for validation
        async *fetchSchemaForValidation({ schemaUrl }: { schemaUrl: string }) {
          const data = await packageEditor.getSchemaForValidation(schemaUrl);
          yield packageEditorSlice.actions.addMonacoSchema({
            key: schemaUrl,
            schema: data
          });
        }
      }
    }
  );
